<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    class="ps-installer-btn"
    :class="[variantClass, sizeClass, { 'ps-installer-btn--block': block }]"
    v-bind="$attrs"
  >
    <span v-if="loading" class="ps-installer-btn__spinner" aria-hidden="true" />
    <span v-if="$slots.icon && !loading" class="ps-installer-btn__icon">
      <slot name="icon" />
    </span>
    <span v-if="$slots.default" class="ps-installer-btn__label">
      <slot />
    </span>
    <span v-if="$slots['icon-right'] && !loading" class="ps-installer-btn__icon">
      <slot name="icon-right" />
    </span>
  </button>
</template>

<script>
export default {
  name: 'InstallerButton',
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'ghost', 'danger', 'success'].includes(value),
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['md', 'lg'].includes(value),
    },
    type: {
      type: String,
      default: 'button',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    variantClass() {
      return `ps-installer-btn--${this.variant}`
    },
    sizeClass() {
      return this.size === 'lg' ? 'ps-installer-btn--lg' : ''
    },
  },
}
</script>

<style scoped>
.ps-installer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.75rem;
  padding: 0 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid transparent;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.ps-installer-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}

.ps-installer-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.ps-installer-btn--block {
  width: 100%;
}

.ps-installer-btn--lg {
  min-height: 3rem;
  padding: 0 1.75rem;
  font-size: 1rem;
}

.ps-installer-btn__icon {
  display: inline-flex;
  flex-shrink: 0;
}

.ps-installer-btn__icon :deep(svg) {
  width: 1rem;
  height: 1rem;
}

.ps-installer-btn--lg .ps-installer-btn__icon :deep(svg) {
  width: 1.125rem;
  height: 1.125rem;
}

.ps-installer-btn__label {
  display: inline-flex;
  align-items: center;
}

.ps-installer-btn__spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ps-installer-spin 0.65s linear infinite;
}

.ps-installer-btn--primary {
  color: #fff;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(37, 99, 235, 0.22);
}

.ps-installer-btn--primary:hover:not(:disabled) {
  background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
}

.ps-installer-btn--secondary {
  color: #334155;
  background: #fff;
  border-color: #cbd5e1;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.ps-installer-btn--secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.ps-installer-btn--ghost {
  color: #475569;
  background: transparent;
}

.ps-installer-btn--ghost:hover:not(:disabled) {
  color: #0f172a;
  background: #f1f5f9;
}

.ps-installer-btn--danger {
  color: #fff;
  background: #dc2626;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(220, 38, 38, 0.18);
}

.ps-installer-btn--danger:hover:not(:disabled) {
  background: #b91c1c;
}

.ps-installer-btn--success {
  color: #fff;
  background: linear-gradient(180deg, #059669 0%, #047857 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(5, 150, 105, 0.22);
}

.ps-installer-btn--success:hover:not(:disabled) {
  background: linear-gradient(180deg, #10b981 0%, #059669 100%);
}

:global(.dark) .ps-installer-btn--secondary {
  color: #e2e8f0;
  background: #1e293b;
  border-color: #475569;
}

:global(.dark) .ps-installer-btn--secondary:hover:not(:disabled) {
  background: #334155;
  border-color: #64748b;
}

:global(.dark) .ps-installer-btn--ghost {
  color: #cbd5e1;
}

:global(.dark) .ps-installer-btn--ghost:hover:not(:disabled) {
  color: #f8fafc;
  background: rgba(51, 65, 85, 0.65);
}

@keyframes ps-installer-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
